import { useEffect, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PLATFORM_ROLE_LABELS, type Permission } from '@azko/shared';
import { api } from '../../lib/api.js';
import { useSession } from '../../lib/session.js';
import { InstallAppButton, usePwa } from '../../pwa/pwa.js';
import { MadeByAzkoGroup, cx } from '../../components/ui.js';
import {
  IconClose,
  IconDashboard,
  IconGauge,
  IconKey,
  IconShield,
  IconHistory,
  IconInbox,
  IconLogout,
  IconMenu,
  IconSettings,
  IconSwap,
  IconTag,
  IconUsers,
} from '../../components/icons.js';
import { CondosMark, CondosWordmark } from '../../components/brand.js';

/**
 * Casca do backoffice da Azko.
 *
 * Separada do `AppShell` de proposito, e nao por estilo: aquele existe para
 * operar UM condominio — menu por modulo contratado, medidor de plano, troca
 * de contexto. Staff nao tem condominio nenhum, entao nada daquilo se aplica;
 * herdar aquele shell obrigaria a encher de `if (staff)`.
 *
 * O que os dois compartilham e a FORMA: sidebar para navegar, topbar para
 * contexto, main largo para a tarefa. A versao anterior era uma coluna
 * centrada de 5xl com abas em pilula — o backoffice e lista, tabela e log, e
 * tudo isso vivia espremido no meio da tela enquanto sobrava margem dos dois
 * lados. Agora o conteudo ocupa a largura que tem.
 *
 * O menu se monta pelas permissoes que a API devolveu, nao por papel. Suporte
 * ve a fila e nao ve o que nao pode decidir — e a mesma lista que o
 * `authorize()` usa para autorizar de verdade, entao a tela nunca oferece um
 * caminho que termina em 403.
 */

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

interface AdminNavItem {
  to: string;
  label: string;
  icon: IconComponent;
  /** `undefined` = todo mundo da equipe ve (o proprio perfil, por exemplo). */
  requires?: Permission;
}

interface AdminNavGroup {
  /** `null` no primeiro grupo: rotulo sobre um item so seria ruido. */
  label: string | null;
  items: AdminNavItem[];
}

const NAV_GROUPS: AdminNavGroup[] = [
  {
    label: null,
    items: [
      {
        to: '/',
        label: 'Visao geral',
        icon: IconDashboard,
        requires: 'plataforma:assinantes:visualizar',
      },
    ],
  },
  {
    label: 'Contas',
    items: [
      {
        to: '/assinantes',
        label: 'Assinantes',
        icon: IconUsers,
        requires: 'plataforma:assinantes:visualizar',
      },
      {
        to: '/solicitacoes',
        label: 'Trocas de plano',
        icon: IconSwap,
        requires: 'plataforma:assinantes:visualizar',
      },
      // Em "Contas" e nao em "Plataforma": a pergunta que esta tela responde e
      // comercial ("de quem eu cobro mais, e quem esta indo embora"), nao
      // operacional. Fica ao lado de quem vai agir sobre a resposta.
      {
        to: '/uso',
        label: 'Uso e limites',
        icon: IconGauge,
        requires: 'plataforma:assinantes:visualizar',
      },
      // Contas fica em "Contas" e nao em "Plataforma" porque a pergunta que
      // traz alguem ate ela e sobre uma PESSOA do cliente, nao sobre o sistema.
      {
        to: '/contas',
        label: 'Contas do sistema',
        icon: IconKey,
        requires: 'plataforma:assinantes:visualizar',
      },
    ],
  },
  {
    label: 'Catalogo',
    items: [
      {
        to: '/planos',
        label: 'Planos',
        icon: IconTag,
        requires: 'plataforma:planos:visualizar',
      },
    ],
  },
  {
    label: 'Plataforma',
    items: [
      {
        to: '/logs',
        label: 'Auditoria',
        icon: IconHistory,
        requires: 'plataforma:logs:visualizar',
      },
      // Ao lado da auditoria e nao dentro de "Contas": as duas respondem a
      // mesma pergunta operacional — o que o sistema fez —, e quem investiga um
      // "nao recebi" pula de uma para a outra.
      {
        to: '/disparos',
        label: 'Disparos',
        icon: IconInbox,
        requires: 'plataforma:logs:visualizar',
      },
      // A matriz de papeis pede a propria permissao, e nao a de logs: ela nao e
      // informacao de suporte, e o mapa de quem pode o que. Quem nao vai mexer
      // nela nao precisa dela aberta.
      {
        to: '/papeis',
        label: 'Papeis e permissoes',
        icon: IconShield,
        requires: 'plataforma:papeis:gerenciar',
      },
    ],
  },
];

/** Configuracoes fica fora dos grupos: e rodape de menu, nao area de trabalho. */
const SETTINGS_ITEM: AdminNavItem = {
  to: '/configuracoes',
  label: 'Configuracoes',
  icon: IconSettings,
};

function Logo({ compact = false }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-3">
      <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-accent text-on-accent">
        <CondosMark className="h-5.5 w-5" />
      </span>
      {!compact && (
        <span className="min-w-0">
          <CondosWordmark height="1.05rem" />
          <span className="mt-1 block truncate text-xs text-muted">Backoffice</span>
        </span>
      )}
    </div>
  );
}

function NavItemLink({ item, onNavigate }: { item: AdminNavItem; onNavigate?: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cx(
          'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition',
          isActive ? 'bg-accent/10 text-fg' : 'text-muted hover:bg-surface hover:text-fg',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Barra de ativo: marca a linha inteira sem fechar o item numa
              borda, que competia com a borda dos cards. */}
          <span
            aria-hidden
            className={cx(
              'absolute left-0 top-1/2 h-5 w-0.75 -translate-y-1/2 rounded-r-full bg-accent transition-opacity',
              isActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <item.icon
            className={cx(
              'size-[1.15rem] shrink-0 transition-colors',
              isActive ? 'text-accent' : 'text-faint group-hover:text-muted',
            )}
          />
          <span className="truncate">{item.label}</span>
        </>
      )}
    </NavLink>
  );
}

function NavList({ onNavigate }: { onNavigate?: () => void }) {
  const { staff } = useSession();
  const permissions = staff?.permissions ?? [];

  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.requires || permissions.includes(item.requires)),
  })).filter((group) => group.items.length > 0);

  return (
    <nav className="space-y-6">
      {groups.map((group, index) => (
        <div key={group.label ?? `group-${index}`}>
          {group.label && (
            <p className="mb-2 px-3 text-[0.68rem] font-bold uppercase tracking-[0.14em] text-faint">
              {group.label}
            </p>
          )}
          <ul className="space-y-0.5">
            {group.items.map((item) => (
              <li key={item.to}>
                <NavItemLink item={item} onNavigate={onNavigate} />
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function StaffChip({ className }: { className?: string }) {
  const { user, staff } = useSession();
  const [leaving, setLeaving] = useState(false);

  if (!user || !staff) return null;

  const roles = staff.roles.map((role) => PLATFORM_ROLE_LABELS[role]).join(', ');

  async function signOut() {
    setLeaving(true);
    try {
      await api.post('/auth/logout');
    } finally {
      window.location.reload();
    }
  }

  return (
    <div className={cx('flex items-center gap-3', className)}>
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-bold">
        {user.initials}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold">{user.name}</span>
        <span className="block truncate text-xs text-muted">{roles || user.email}</span>
      </span>
      <button
        type="button"
        onClick={() => void signOut()}
        disabled={leaving}
        title="Sair"
        aria-label="Sair"
        className="shrink-0 rounded-lg p-2 text-faint transition hover:bg-surface-2 hover:text-fg disabled:opacity-50"
      >
        <IconLogout className="size-[1.15rem]" />
      </button>
    </div>
  );
}

/** Conteudo da sidebar — reaproveitado pela gaveta do mobile. */
function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <>
      <div className="shrink-0 px-5 py-6">
        <Logo />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        <NavList onNavigate={onNavigate} />
      </div>

      <div className="shrink-0 space-y-3 border-t border-line-soft p-3">
        <InstallAppButton />
        <NavItemLink item={SETTINGS_ITEM} onNavigate={onNavigate} />
        <StaffChip className="px-2" />
        <MadeByAzkoGroup className="px-2 pb-1" />
      </div>
    </>
  );
}

export function AdminShell({ children }: { children: ReactNode }) {
  const { staff } = useSession();
  const { isOnline } = usePwa();
  const [menuOpen, setMenuOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => setMenuOpen(false), [pathname]);

  useEffect(() => {
    if (!menuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  if (!staff) return null;

  return (
    <div className="safe-screen lg:grid lg:grid-cols-[17rem_minmax(0,1fr)]">
      {/* Sidebar — desktop */}
      <aside className="safe-area-y sticky top-0 hidden h-dvh flex-col border-r border-line-soft bg-sidebar lg:flex">
        <SidebarContent />
      </aside>

      {/* Gaveta — mobile. Sobrepoe em vez de empurrar o conteudo. */}
      <div
        className={cx(
          'fixed inset-0 z-40 lg:hidden',
          menuOpen ? 'pointer-events-auto' : 'pointer-events-none',
        )}
        aria-hidden={!menuOpen}
        inert={!menuOpen}
      >
        <button
          type="button"
          tabIndex={menuOpen ? 0 : -1}
          aria-label="Fechar menu"
          onClick={() => setMenuOpen(false)}
          className={cx(
            'absolute inset-0 bg-ink/70 backdrop-blur-sm transition-opacity',
            menuOpen ? 'opacity-100' : 'opacity-0',
          )}
        />
        <div
          className={cx(
            'safe-area-y absolute inset-y-0 left-0 flex w-68 max-w-[85vw] flex-col border-r border-line-soft bg-sidebar transition-transform duration-200',
            menuOpen ? 'translate-x-0' : '-translate-x-full',
          )}
        >
          <SidebarContent onNavigate={() => setMenuOpen(false)} />
        </div>
      </div>

      <div className="safe-screen flex flex-col">
        {/* Topbar — a faixa de contexto. No backoffice ela responde "eu estou
            fora de qualquer condominio, olhando a plataforma inteira", que e o
            que mais confunde quem alterna entre os dois produtos. */}
        <header className="safe-area-top sticky top-0 z-30 border-b border-line-soft bg-ink/80 backdrop-blur">
          <div className="mx-auto flex h-16 max-w-360 items-center gap-3 pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] sm:pl-[max(2rem,env(safe-area-inset-left))] sm:pr-[max(2rem,env(safe-area-inset-right))]">
            <button
              type="button"
              aria-label={menuOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((open) => !open)}
              className="-ml-1 shrink-0 rounded-xl p-2 text-muted transition hover:bg-surface hover:text-fg lg:hidden"
            >
              {menuOpen ? <IconClose className="size-5" /> : <IconMenu className="size-5" />}
            </button>

            <div className="lg:hidden">
              <Logo compact />
            </div>

            <span className="ml-auto flex min-w-0 items-center gap-2.5 lg:ml-0">
              <span
                aria-hidden
                className={cx('size-1.5 shrink-0 rounded-full', isOnline ? 'bg-accent' : 'bg-warn')}
              />
              <span className="truncate text-sm font-bold">Equipe Azko</span>
              <span
                className={cx('hidden shrink-0 text-xs sm:inline', isOnline ? 'text-faint' : 'text-warn')}
              >
                {isOnline ? 'Acesso global — fora do contexto de qualquer condominio' : 'Sem conexão'}
              </span>
            </span>
          </div>
        </header>

        <main className="mx-auto w-full max-w-360 flex-1 space-y-6 py-8 pl-[max(1.25rem,env(safe-area-inset-left))] pr-[max(1.25rem,env(safe-area-inset-right))] sm:pl-[max(2rem,env(safe-area-inset-left))] sm:pr-[max(2rem,env(safe-area-inset-right))]">
          {children}
        </main>
      </div>
    </div>
  );
}
